import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ShoppingBag, User, Heart, Menu, X, LogOut, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import Logo from "@/components/Logo";

export default function Header() {
  const { t, cartCount, setCartOpen, user, logout } = useApp();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [userMenuOpen]);

  const doLogout = () => {
    logout();
    setUserMenuOpen(false);
    nav("/", { replace: true });
  };

  const navLinks = [
    { to: "/", label: t("nav_home") },
    { to: "/shop", label: t("nav_shop") },
    { to: "/booking", label: t("booking_eyebrow") },
    { to: "/about", label: t("nav_about") },
    { to: "/contact", label: t("nav_contact") },
  ];

  return (
    <>
      <div className="bg-[#0A0A0A] border-b border-white/5 text-white/60 relative z-[70]">
        <div className="container-narrow flex items-center justify-between gap-3 py-2 text-[11px] tracking-[0.25em] uppercase">
          <span className="hidden sm:inline truncate">{t("free_shipping_over")}</span>
          <span className="sm:hidden truncate">{t("free_shipping_over")}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  data-testid="top-user-menu"
                  className="hidden xs:inline-flex sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors"
                >
                  <User size={12} strokeWidth={2} />
                  {t("my_profile")}
                  <ChevronDown size={11} strokeWidth={2} className={`transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute end-0 top-full mt-1 min-w-[180px] bg-[#0F0F0F] border border-gold/30 shadow-2xl z-[60]" data-testid="top-user-dropdown">
                    <Link to="/account" onClick={() => setUserMenuOpen(false)} data-testid="top-account-link"
                      className="flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase text-white/80 hover:bg-gold/10 hover:text-gold transition-colors">
                      <User size={12} /> {t("my_profile")}
                    </Link>
                    <div className="h-px bg-white/10" />
                    <button onClick={doLogout} data-testid="top-logout-btn"
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase text-white/80 hover:bg-gold/10 hover:text-gold transition-colors">
                      <LogOut size={12} /> {t("logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                data-testid="top-login-btn"
                className="hidden xs:inline-flex sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors"
              >
                <User size={12} strokeWidth={2} />
                {t("login")}
              </Link>
            )}
          </div>
        </div>
      </div>
      <header data-testid="site-header" className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}>
        <div className="container-narrow flex items-center justify-between h-20 md:h-24 gap-4">
          <button data-testid="mobile-menu-toggle" className="md:hidden text-white p-2" onClick={() => setMobileOpen(o => !o)} aria-label={t("aria_menu")}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Logo size="lg" className="flex-shrink-0" />
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map(l => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-${l.label}`}
                className={({isActive}) => `link-underline text-[13px] tracking-[0.15em] uppercase ${isActive ? "text-gold" : "text-white/85 hover:text-white"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 md:gap-4">
            <div className="hidden lg:flex items-center gap-2">
              <CurrencySwitcher />
            </div>
            <Link to="/wishlist" data-testid="wishlist-link" aria-label={t("aria_wishlist")} className="text-white/85 hover:text-gold transition-colors">
              <Heart size={20} strokeWidth={1.5} />
            </Link>
            <Link to={user ? "/account" : "/auth"} data-testid="account-link" aria-label={t("aria_account")} className="text-white/85 hover:text-gold transition-colors">
              <User size={20} strokeWidth={1.5} />
            </Link>
            <button data-testid="cart-toggle" onClick={() => setCartOpen(true)} className="relative text-white/85 hover:text-gold transition-colors" aria-label={t("aria_cart")}>
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-gold text-black text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-medium">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
            <div className="container-narrow py-6 flex flex-col gap-4">
              {navLinks.map(l => (
                <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-${l.label}`}
                  className={({isActive}) => `text-[13px] tracking-[0.15em] uppercase py-2 ${isActive ? "text-gold" : "text-white"}`}>
                  {l.label}
                </NavLink>
              ))}
              <Link to="/order-tracking" className="text-[13px] tracking-[0.15em] uppercase py-2 text-white/70">{t("nav_tracking")}</Link>
              <Link to="/consultation" className="text-[13px] tracking-[0.15em] uppercase py-2 text-white/70">{t("nav_consultation")}</Link>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <CurrencySwitcher />
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
